'use client';

import {useState, useEffect, useRef} from 'react';
import {useSearchParams} from 'next/navigation';
import dynamic from 'next/dynamic';
import MapSidebar from '@/components/map/MapSidebar';
import DelegationPanel from '@/components/map/DelegationPanel';
import type {Projet, DelegationInfo} from '@/components/map/TunisiaMap';

const TunisiaMap = dynamic(() => import('@/components/map/TunisiaMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center bg-surface animate-pulse">
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-primary/20 mx-auto mb-3 animate-pulse" />
        <p className="text-sm text-muted">Chargement de la carte...</p>
      </div>
    </div>
  ),
});

const categories = [
  {value: '', label: 'Toutes categories'},
  {value: 'sante', label: 'Sante'},
  {value: 'education', label: 'Education'},
  {value: 'environnement', label: 'Environnement'},
  {value: 'social', label: 'Social'},
  {value: 'infrastructure', label: 'Infrastructure'},
  {value: 'culture', label: 'Culture'},
];

const statuts = [
  {value: '', label: 'Tous statuts'},
  {value: 'en_collecte', label: 'En collecte'},
  {value: 'finance', label: 'Finance'},
  {value: 'urgent', label: 'Urgent'},
  {value: 'termine', label: 'Termine'},
];

export default function StructuresPage() {
  const searchParams = useSearchParams();
  const projetIdParam = searchParams.get('projet');
  const [filters, setFilters] = useState({categorie: '', statut: '', urgentOnly: false});
  const [selectedProjet, setSelectedProjet] = useState<Projet | null>(null);
  const [selectedDeleg, setSelectedDeleg] = useState<DelegationInfo | null>(null);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [viewMode, setViewMode] = useState<'carte' | 'liste'>('carte');
  const autoSelectedRef = useRef(false);

  const panelOpen = !!(selectedProjet || selectedDeleg);

  /* Hide global breadcrumb + footer on this fullscreen map page */
  useEffect(() => {
    const breadcrumb = document.querySelector('nav[aria-label="Breadcrumb"]') as HTMLElement;
    const footer = document.querySelector('footer') as HTMLElement;
    if (breadcrumb) breadcrumb.style.display = 'none';
    if (footer) footer.style.display = 'none';
    return () => {
      if (breadcrumb) breadcrumb.style.display = '';
      if (footer) footer.style.display = '';
    };
  }, []);

  useEffect(() => {
    fetch('/api/v1/carte/projets')
      .then(r => r.ok ? r.json() : null)
      .then(geojson => {
        if (!geojson?.features) return;
        setProjets(geojson.features.map((f: { properties: Record<string, unknown>; geometry: { coordinates: number[] } }) => {
          const p = f.properties;
          return {
            id: p.id as number,
            titre: (p.titre || '') as string,
            categorie: (p.categorie || '') as string,
            statut: (p.statut || 'en_collecte') as string,
            budget_objectif: (p.budget_objectif || 0) as number,
            montant_collecte: (p.montant_collecte || 0) as number,
            gouvernorat: (p.gouvernorat || '') as string,
            delegation: (p.delegation || '') as string,
            imada: (p.imada || '') as string,
            jalons: [],
            photos: [],
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
          };
        }));
      })
      .catch(() => {});
  }, []);

  /* Auto-select projet from ?projet=ID */
  useEffect(() => {
    if (!projetIdParam || projets.length === 0 || autoSelectedRef.current) return;
    const target = projets.find(p => String(p.id) === projetIdParam);
    if (target) {
      setSelectedProjet(target);
      autoSelectedRef.current = true;
    }
  }, [projetIdParam, projets]);

  const filteredCount = projets.filter(p => {
    if (filters.categorie && p.categorie !== filters.categorie) return false;
    if (filters.statut && p.statut !== filters.statut) return false;
    if (filters.urgentOnly && p.statut !== 'urgent') return false;
    return true;
  }).length;

  return (
    <div className="flex flex-col" style={{height: 'calc(100vh - 110px)'}}>
      {/* Map OR List view */}
      {viewMode === 'liste' ? (
        <div className="flex-1 overflow-y-auto p-4 bg-surface">
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projets.filter(p => {
              if (filters.categorie && p.categorie !== filters.categorie) return false;
              if (filters.statut && p.statut !== filters.statut) return false;
              if (filters.urgentOnly && p.statut !== 'urgent') return false;
              return true;
            }).map(p => {
              const pct = p.budget_objectif > 0 ? Math.round((p.montant_collecte / p.budget_objectif) * 100) : 0;
              return (
                <div key={p.id} className="bg-white rounded-xl border border-border p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedProjet(p); setViewMode('carte'); }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.statut === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-primary/10 text-primary'}`}>{p.statut}</span>
                    <span className="text-[10px] text-muted capitalize">{p.categorie}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1 line-clamp-2">{p.titre}</h3>
                  <p className="text-xs text-muted mb-2">{p.gouvernorat} &gt; {p.delegation}</p>
                  <div className="h-1.5 bg-border rounded-full overflow-hidden mb-1">
                    <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : 'bg-primary'}`} style={{width: `${Math.min(pct, 100)}%`}} />
                  </div>
                  <div className="flex justify-between text-[10px] text-muted">
                    <span>{p.montant_collecte.toLocaleString()} TND</span>
                    <span className="font-medium text-primary">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
      <div className="flex flex-1 min-h-0 relative">
        {/* Map container */}
        <div className={`relative transition-all duration-300 ${panelOpen ? 'flex-1' : 'w-full'}`}>
          <TunisiaMap
            filters={filters}
            focusPoint={selectedProjet && autoSelectedRef.current ? {lng: selectedProjet.lng, lat: selectedProjet.lat} : undefined}
            onSelectProjet={(p) => { setSelectedProjet(p); setSelectedDeleg(null); }}
            onSelectDelegation={(d) => { setSelectedDeleg(d); setSelectedProjet(null); }}
            projets={projets}
          />

          {/* Overlay: Filters on map */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
            <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-lg px-3 py-2 flex flex-wrap items-center gap-2">
              <select
                value={filters.categorie}
                onChange={e => setFilters({...filters, categorie: e.target.value})}
                className="px-2 py-1 text-xs border border-gray-200 rounded bg-white text-gray-700 focus:ring-1 focus:ring-[#1B5E20] outline-none"
              >
                {categories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <select
                value={filters.statut}
                onChange={e => setFilters({...filters, statut: e.target.value})}
                className="px-2 py-1 text-xs border border-gray-200 rounded bg-white text-gray-700 focus:ring-1 focus:ring-[#1B5E20] outline-none"
              >
                {statuts.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={filters.urgentOnly}
                  onChange={e => setFilters({...filters, urgentOnly: e.target.checked})}
                  className="w-3 h-3 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <span className="w-1.5 h-1.5 rounded-full bg-red-600 inline-block" />
                Urgent
              </label>
              <span className="text-[10px] text-gray-400 ml-1">{filteredCount} projets</span>
              <div className="flex rounded-md overflow-hidden border border-gray-200 ml-2">
                <button onClick={() => setViewMode('carte')} className={`px-2 py-0.5 text-[10px] ${viewMode === 'carte' ? 'bg-primary text-white' : 'bg-white text-gray-500'}`}>Carte</button>
                <button onClick={() => setViewMode('liste')} className={`px-2 py-0.5 text-[10px] ${viewMode !== 'carte' ? 'bg-primary text-white' : 'bg-white text-gray-500'}`}>Liste</button>
              </div>
            </div>
          </div>
        </div>

        {/* Side panel — 360px fixed when open */}
        {panelOpen && (
          <div className="w-[360px] max-w-[35%] border-s border-border bg-white overflow-hidden flex flex-col shrink-0">
            {selectedDeleg && (
              <DelegationPanel
                delegation={selectedDeleg}
                projets={projets}
                onClose={() => setSelectedDeleg(null)}
                onSelectProjet={(p) => { setSelectedProjet(p); setSelectedDeleg(null); }}
              />
            )}
            {selectedProjet && !selectedDeleg && (
              <MapSidebar
                projet={selectedProjet}
                onClose={() => setSelectedProjet(null)}
              />
            )}
          </div>
        )}
      </div>

      )}

      {/* Compact footer */}
      <div className="shrink-0 bg-[#0D3B12] text-white/70 px-4 py-2 flex items-center justify-between text-xs border-t border-white/10">
        <div className="flex items-center gap-3">
          <img src="/images/utss/rose.svg" alt="UTSS" className="h-5 w-auto opacity-60" />
          <span>&copy; 2026 UTSS — Union Tunisienne de Solidarite Sociale</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="tel:+21671808066" className="hover:text-white transition-colors">(+216) 71 808 066</a>
          <a href="mailto:utss@planet.tn" className="hover:text-white transition-colors">utss@planet.tn</a>
          <span className="text-white/30">|</span>
          <a href="#" className="hover:text-white transition-colors" aria-label="Facebook">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a href="#" className="hover:text-white transition-colors" aria-label="YouTube">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/></svg>
          </a>
        </div>
      </div>
    </div>
  );
}
